/**
Author - Venkatesh Pullaganti
Git - https://github.com/venkatesh1146
**/

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import parse from 'html-react-parser';
import styled from 'styled-components';

import {ActionButton, ActionButtonSecondary, Dropdown} from 'screens/commons';

import IMAGES from 'constants/Images';

const propTypes = {
  handleImageUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  showAnimation: PropTypes.bool.isRequired,
  handleDeleteUploadedFile: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string.isRequired,
  documentOptions: PropTypes.array,
  allowPasswordProtected: PropTypes.bool,
  setSelectedOption: PropTypes.func.isRequired,
  selectedOption: PropTypes.object,
  password: PropTypes.string,
  setPassword: PropTypes.func,
  uploadMessage: PropTypes.string,
  goBackButton: PropTypes.object,
  instructions: PropTypes.string,
  showUploadSection: PropTypes.bool,
  referenceImage: PropTypes.string,
  successStyleOnUpload: PropTypes.bool,
};

const UploadDocument = ({
  declarationsList,
  isLoading,
  isDisabled,
  handleImageUpload,
  uploadedFiles,
  showAnimation,
  handleDeleteUploadedFile,
  onSubmit,
  heading = 'Upload Your Document',
  subHeading = 'Please provide the requested document in one of the supported formats',
  documentOptions = [],
  allowPasswordProtected,
  setSelectedOption,
  selectedOption,
  password,
  setPassword,
  errorMessage = '',
  uploadMessage = '',
  goBackButton = {
    buttonText: '',
    callback: () => {},
    info: '',
    show: false,
  },
  instructions,
  prependContent,
  showUploadSection = true,
  referenceImage,
  successStyleOnUpload = false,
}) => {
  const [selectedOptionInfo, setSelectedOptionInfo] = useState(selectedOption?.text || '');
  const [isDragging, setIsDragging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dropdownOptions = documentOptions.length
    ? documentOptions?.map((eachDeclaration) => (
        <EachOption
          key={eachDeclaration.id || eachDeclaration.value}
          className="body-medium"
          onClick={() => {
            setSelectedOption(eachDeclaration);
            setSelectedOptionInfo(eachDeclaration?.text);
          }}
        >
          {parse(eachDeclaration?.render)}
        </EachOption>
      ))
    : [];

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (acceptedFiles) => {
    setIsDragging(false);
    handleImageUpload(acceptedFiles);
  };

  return (
    <Wrapper className="custom-scrollbar upload-doc-wrapper">
      <SubTitle dangerouslySetInnerHTML={{__html: subHeading}} />
      <CardContent className="upload-doc-content">
        {prependContent && prependContent}
        {instructions && <FormattedHTML dangerouslySetInnerHTML={{__html: instructions}} />}
        {documentOptions.length > 0 && (
          <>
            {referenceImage && (
              <ReferenceImage src={referenceImage} alt="Document reference" />
            )}
            <InfoText>
              {selectedOptionInfo ? parse(selectedOptionInfo) : 'Select document type'}
            </InfoText>
            <Dropdown
              options={dropdownOptions}
              title={selectedOption?.render ? parse(selectedOption?.render) : 'Choose an option'}
              size="small"
            />
          </>
        )}
        {showUploadSection ? (
          <Dropzone
          onDrop={handleDrop}
          multiple={false}
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'application/pdf': ['.pdf'],
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {({getRootProps, getInputProps}) => (
            <DropZoneContainer
              {...getRootProps()}
              isDragging={isDragging}
              hasFiles={uploadedFiles.length > 0}
              isUploadSuccess={uploadedFiles.length > 0 && successStyleOnUpload}
            >
              <FileInput {...getInputProps()} id="file-upload" />

              {uploadedFiles.length === 0 ? (
                showAnimation ? (
                  <DropzoneContent>
                    <span>Uploading...</span>
                  </DropzoneContent>
                ) : (
                  <DropzoneContent>
                    {uploadMessage ? (
                      <FileInfoBox>
                        <FileInfoText dangerouslySetInnerHTML={{__html: uploadMessage}} />
                      </FileInfoBox>
                    ) : null}
                    <MainUploadSection>
                      <UploadIconContainer>
                        <UploadIcon src={IMAGES.fileUploadImage} alt="Upload" />
                      </UploadIconContainer>
                      <TextContainer>
                        <UploadDescription>Drag 'n' drop the document here or</UploadDescription>
                        <UploadButton className="mt-3">Click to select files</UploadButton>
                      </TextContainer>
                    </MainUploadSection>
                  </DropzoneContent>
                )
              ) : (
                uploadedFiles.map((eachFile) => (
                  <FilePreview key={eachFile.id}>
                    <FileHeader>
                      <FileIcon src={IMAGES.fileIcon} alt="File" />
                      <FileName>{eachFile.name}</FileName>
                    </FileHeader>
                    <FileActions>
                      <ActionBtns>
                        <FileButton
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById('file-upload').click();
                          }}
                        >
                          Change File
                        </FileButton>
                        <FileButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUploadedFile(eachFile);
                          }}
                        >
                          Remove
                        </FileButton>
                      </ActionBtns>
                    </FileActions>
                  </FilePreview>
                ))
              )}
              <SupportedFormats>
                Supported file formats include only png, jpg, jpeg and pdf
              </SupportedFormats>
              <FileSize>
                File size limit should not exceed 6.0 MB
              </FileSize>
            </DropZoneContainer>
          )}
        </Dropzone>
        ) : null}

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        {showUploadSection ? (
        <PasswordBox>
          {allowPasswordProtected ? (
            <>
              <PasswordLabel className="mb-2">
                <LockIcon src={IMAGES.lockIcon} alt="Lock" />
                <PasswordText>
                  Attention: Please provide password in the below input if it is a
                  password-protected document.
                </PasswordText>
              </PasswordLabel>
              <PasswordInputContainer>
                <PasswordInput
                  placeholder="Enter password"
                  type={showPassword ? 'text' : 'password'}
                  value={password || ''}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <TogglePasswordButton type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? 'Hide' : 'Show'}
                </TogglePasswordButton>
              </PasswordInputContainer>
            </>
          ) : (
            <PasswordLabel>
              <LockIcon src={IMAGES.lockIcon} alt="Lock" />
              <PasswordText>
                Attention: Please do not upload password-protected documents.
              </PasswordText>
            </PasswordLabel>
          )}
        </PasswordBox>
        ) : null}
      </CardContent>

      <CardFooter className="upload-doc-footer">
        <ActionButton
          buttonText="Upload & Continue"
          callback={onSubmit}
          isLoading={isLoading}
          isDisabled={isLoading || isDisabled}
          className="my-2 upload-doc-button"
        />
        {goBackButton?.show && (
          <ActionButtonSecondary
            className="mt-2"
            callback={goBackButton.callback}
            buttonText={goBackButton?.buttonText || 'Go Back'}
          />
        )}
        {goBackButton?.show && goBackButton?.info && (
          <div style={{marginTop: '1.6rem', textAlign: 'center'}}>
            <div dangerouslySetInnerHTML={{__html: goBackButton?.info}} />
          </div>
        )}
      </CardFooter>
    </Wrapper>
  );
};

// EachOption component for dropdown
const EachOption = styled.li`
  text-decoration: none;
  padding: 0.7rem;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.background.secondary};
    color: ${(props) => props.theme.colors.brand.primary};
  }

  /* Style any nested HTML from the parsed content */
  p,
  span {
    margin: 0;
    padding: 0;
  }

  b,
  strong {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  }
`;

UploadDocument.propTypes = propTypes;

export default UploadDocument;

const Wrapper = styled.div`
  padding: 1.5rem;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;

  .warning-text {
    color: #eb7134 !important;
    font-weight: 600;
  }
  .heading-text {
    padding: 1.5rem;
  }
`;

const SubTitle = styled.p`
  margin: 0.5rem 0 0;
  padding: 0;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const FormattedHTML = styled.div`
  margin: 1rem 0 2rem 0;
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};

  ul {
    padding-left: 2rem;
    margin: 0.5rem 0;
  }

  li {
    margin-bottom: 0.5rem;
    position: relative;
    list-style-type: none;
    padding-left: 1rem;
    text-align: justify;
    text-justify: inter-word; /* Better word spacing */
    hyphens: auto; /* Enable hyphenation */

    &:before {
      content: '';
      position: absolute;
      top: 0.5rem;
      left: -1rem;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background-color: currentColor;
    }
  }

  span[style*='color:#eb7134'] {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #eb7134;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  b {
    font-weight: ${(props) => props.theme.typography.fontWeight.semiBold};
    display: block;
    margin-top: 0.5rem;
    color: ${(props) => props.theme.colors.text.secondary};
  }
`;
const CardContent = styled.div`
  padding: 1.5rem;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem 2rem;
`;

const InfoText = styled.p`
  margin: 0.5rem 0;
  margin-top: 1rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.brand.primary};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
`;

const ReferenceImage = styled.img`
  display: block;
  width: 100%;
  max-width: 30rem;
  margin: 1rem auto 1.5rem;
`;

const DropZoneContainer = styled.div`
  border: 2px dashed
    ${({isDragging, isUploadSuccess, theme}) =>
      isDragging
        ? theme.colors.brand.primary
        : isUploadSuccess
          ? theme.colors.feedback.success
          : '#ddc9f4'};
  border-radius: 0.8rem;
  padding: 2.4rem;
  padding-bottom: 1rem;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  margin: 1.6rem 0 0 0;
  background-color: ${({hasFiles, isUploadSuccess, theme}) =>
    isUploadSuccess
      ? theme.colors.feedback.successLight
      : hasFiles
        ? '#F8F2FF'
        : '#ffffff'};
  &:hover {
    border-color: ${({isUploadSuccess, theme}) =>
      isUploadSuccess ? theme.colors.feedback.success : '#a6a6a6b5'};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1.6rem;
`;

const MainUploadSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.6rem;
  background-color: #f8fafc;
  border: 1px solid #8670cc;
  border-radius: 0.6rem;
  padding: 2.5rem;
  width: 100%;
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const TextContainer = styled.div`
  text-align: left;
`;

const UploadIconContainer = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.brand.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 4.8rem;
    height: 4.8rem;
  }
`;

const UploadIcon = styled.img`
  width: 3.2rem;
  height: 3.2rem;
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

const UploadButton = styled.button`
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 0.8rem 1.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  margin-bottom: 0.8rem;
  display: inline-block;
  text-align: center;

  &:hover {
    background-color: #f8fafc;
  }
`;

const UploadDescription = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0;
`;

const FileInfoBox = styled.div`
  margin: 0 auto;
  padding: 0;
  text-align: center;
  width: 100%;
`;

const FileInfoText = styled.p`
  margin: 0.4rem 0;
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.primary};

  b {
    font-weight: 500 !important;
    font-size: ${(props) => props.theme.typography.fontSize.sm} !important;
  }
`;

const FilePreview = styled.div`
  width: 100%;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.6rem;
  width: 100%;
`;

const FileIcon = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`;

const FileName = styled.span`
  font-size: ${(props) => props.theme.typography.fontSize.md};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.primary};
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  flex: 1;
  text-align: left;
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-self: center;
  margin: 0 auto;
  flex-grow: 1;
`;

const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`;

const FileButton = styled.button`
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 0.8rem 1.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;

  &:hover {
    background-color: #f8fafc;
  }
`;

const PasswordBox = styled.div`
  margin: 1.6rem 0;
  background-color: ${(props) => props.theme.colors.background.secondary};
  padding: 1rem;
  border-radius: 0.6rem;
`;

const PasswordLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
`;

const LockIcon = styled.img`
  width: 1.2rem;
  height: 1.2rem;
  margin-top: 0.2rem;
`;

const PasswordText = styled.p`
  margin: 0;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.brand.tertiary};
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.primary};
  outline: none;
  transition: all 0.2s ease-in-out;

  &::placeholder {
    color: ${(props) => props.theme.colors.text.secondary};
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.brand.primary};
    box-shadow: 0 0 0 2px ${(props) => `${props.theme.colors.brand.primary}1A`};
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  padding: 0 1.2rem;
  background: none;
  border: none;
  cursor: pointer;
`;

const SupportedFormats = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  color: ${(props) => props.theme.colors.text.secondary};
  margin-top: 1rem;
`;

const FileSize = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  color: ${(props) => props.theme.colors.text.secondary};
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.p`
  display: inline-flex;
  align-items: center;
  background-color: ${(props) => `${props.theme.colors.feedback.error}15`};
  color: ${(props) => props.theme.colors.feedback.error};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  margin: 0.8rem 0;
  border: 1px solid ${(props) => `${props.theme.colors.feedback.error}20`};

  &::before {
    content: '⚠️';
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
`;

const FileInput = styled.input`
  display: none;
`;
